import { useEffect, useState } from 'react'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  FolderOpen,
  Plus,
  Trash2,
  Globe,
  Lock,
  Package,
  Search,
  AlertCircle,
  X,
  ExternalLink,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface CollectionItem {
  package_id: string
  package_name?: string
  package_slug?: string
  package_summary?: string
  version_constraint: string
}

interface Collection {
  collection_id: string
  name: string
  description?: string
  is_public: boolean
  items: CollectionItem[]
  item_count?: number
  created_at?: string
}

interface SearchResult {
  package_id: string
  slug: string
  name: string
  summary?: string
}

export default function PublisherCollectionsPage() {
  const { token, publisher } = usePublisherAuth()
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newIsPublic, setNewIsPublic] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)

  // Add item modal
  const [addItemCollId, setAddItemCollId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)

  // Expanded collection
  const [expandedColl, setExpandedColl] = useState<string | null>(null)

  useEffect(() => {
    loadCollections()
  }, [token])

  async function loadCollections() {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE_URL}/v1/publisher/me/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load collections')
      const data = await res.json()
      setCollections(data?.items || [])
    } catch {
      // Fallback: try the general collections endpoint
      try {
        const res = await fetch(`${API_BASE_URL}/v1/collections`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          // Filter to only collections owned by this publisher
          const mine = (data?.items || []).filter(
            (c: Collection & { owner_id?: string }) =>
              c.owner_id === publisher?.publisher_id
          )
          setCollections(mine)
        }
      } catch {
        setCollections([])
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!token || !newName) return
    setCreateLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/v1/collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          org_id: publisher?.publisher_id || '',
          name: newName,
          description: newDescription,
          is_public: newIsPublic,
        }),
      })
      if (!res.ok) throw new Error('Failed to create collection')
      setShowCreateModal(false)
      setNewName('')
      setNewDescription('')
      setNewIsPublic(false)
      await loadCollections()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create'
      setError(msg)
    } finally {
      setCreateLoading(false)
    }
  }

  async function handleDelete(collId: string) {
    if (!token) return
    try {
      await fetch(`${API_BASE_URL}/v1/collections/${collId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      await loadCollections()
    } catch {
      setError('Failed to delete collection')
    }
  }

  async function searchPackages(query: string) {
    if (!query || query.length < 2) {
      setSearchResults([])
      return
    }
    setSearchLoading(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/packages/search?q=${encodeURIComponent(query)}`,
      )
      if (res.ok) {
        const data = await res.json()
        setSearchResults(data?.items || [])
      }
    } catch {
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  async function addItemToCollection(collId: string, pkg: SearchResult) {
    if (!token) return
    try {
      await fetch(`${API_BASE_URL}/v1/collections/${collId}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: pkg.package_id,
          version_constraint: '^1.0.0',
        }),
      })
      setAddItemCollId(null)
      setSearchQuery('')
      setSearchResults([])
      await loadCollections()
    } catch {
      setError('Failed to add item')
    }
  }

  async function removeItemFromCollection(collId: string, packageId: string) {
    if (!token) return
    try {
      await fetch(
        `${API_BASE_URL}/v1/collections/${collId}/items/${packageId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      await loadCollections()
    } catch {
      setError('Failed to remove item')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
          <p className="text-gray-500 mt-1">
            Organize skills into collections for personal use or to share
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Collection
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="card p-8 text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-500">Loading collections...</p>
        </div>
      ) : collections.length === 0 ? (
        <div className="card p-8 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No collections yet
          </h3>
          <p className="text-gray-500 mb-4">
            Create a collection to organize MCP skills for your projects or to
            share with others.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Collection
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {collections.map((coll) => (
            <div key={coll.collection_id} className="card">
              <div
                className="p-5 cursor-pointer hover:bg-gray-50"
                onClick={() =>
                  setExpandedColl(
                    expandedColl === coll.collection_id
                      ? null
                      : coll.collection_id,
                  )
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FolderOpen className="w-5 h-5 text-primary-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {coll.name}
                      </h3>
                      {coll.description && (
                        <p className="text-sm text-gray-500">
                          {coll.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      {coll.is_public ? (
                        <Globe className="w-3 h-3" />
                      ) : (
                        <Lock className="w-3 h-3" />
                      )}
                      {coll.is_public ? 'Public' : 'Private'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {coll.items?.length || 0} skills
                    </span>
                  </div>
                </div>
              </div>

              {expandedColl === coll.collection_id && (
                <div className="border-t border-gray-200 p-5">
                  {coll.items && coll.items.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {coll.items.map((item) => (
                        <div
                          key={item.package_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {item.package_name || item.package_id}
                            </span>
                            {item.package_slug && (
                              <a
                                href={`/skills/${item.package_slug}`}
                                className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                View
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() =>
                              removeItemFromCollection(
                                coll.collection_id,
                                item.package_id,
                              )
                            }
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">
                      No skills in this collection yet
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddItemCollId(coll.collection_id)}
                      className="btn btn-secondary text-sm inline-flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Skill
                    </button>
                    <button
                      onClick={() => handleDelete(coll.collection_id)}
                      className="btn text-sm text-red-600 border border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              New Collection
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="My Collection"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm h-20"
                  placeholder="What is this collection for?"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setNewIsPublic(false)}
                  className={`flex-1 p-3 border rounded-lg flex items-center justify-center gap-2 text-sm ${
                    !newIsPublic
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200'
                  }`}
                >
                  <Lock className="w-4 h-4" />
                  Private
                </button>
                <button
                  type="button"
                  onClick={() => setNewIsPublic(true)}
                  className={`flex-1 p-3 border rounded-lg flex items-center justify-center gap-2 text-sm ${
                    newIsPublic
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  Public
                </button>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || !newName}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {addItemCollId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Add Skill to Collection
            </h2>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  searchPackages(e.target.value)
                }}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Search for skills..."
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {searchLoading ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  Searching...
                </p>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  {searchQuery.length >= 2
                    ? 'No skills found'
                    : 'Type to search for skills'}
                </p>
              ) : (
                searchResults.map((pkg) => (
                  <button
                    key={pkg.package_id}
                    onClick={() => addItemToCollection(addItemCollId, pkg)}
                    className="w-full text-left p-3 rounded-lg hover:bg-primary-50 border border-transparent hover:border-primary-200 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {pkg.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {pkg.summary || pkg.slug}
                    </p>
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-end pt-4">
              <button
                onClick={() => {
                  setAddItemCollId(null)
                  setSearchQuery('')
                  setSearchResults([])
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
